import { type AnyAction } from "redux";
import { type ShopfrontDispatch } from "../../_Bootstrap/hooks";
import { CancelOptionButton } from "../../Common/Buttons/Button";
import { isDarkMode } from "../../Common/DarkMode/DarkModeHooks";
import NoTranslate from "../../Common/NoTranslate";
import PopupManager from "../../Common/Popups/PopupManager";
import { determineDataSource, doesDatabaseNeedUpdating } from "../../Data/DataStores/DataSource";
import {
    type SalePayment,
    type SalePaymentStatus,
    type SaleState,
    type SaleVoucherProduct,
} from "../../Reducers/SellScreen/SellScreenSaleReducerTypes";
import { RequestGiftCard } from "../../Routes/SellScreen/Dialogs/GiftCard/RequestGiftCard";
import { RequestPayGiftCard } from "../../Routes/SellScreen/Dialogs/GiftCard/RequestPayGiftCard";
import { RequestSelectGiftCard } from "../../Routes/SellScreen/Dialogs/GiftCard/RequestSelectGiftCard";
import Integration from "../../Routes/SellScreen/Dialogs/Payments/Integration";
import RequestCashout from "../../Routes/SellScreen/Dialogs/Payments/RequestCashout";
import RequestLoyalty from "../../Routes/SellScreen/Dialogs/Payments/RequestLoyalty";
import RequestRefund from "../../Routes/SellScreen/Dialogs/Payments/RequestRefund";
import { RevertPayment } from "../../Routes/SellScreen/Dialogs/Payments/RevertPayment";
import RefundReason from "../../Routes/SellScreen/Dialogs/RefundReason";
import { PromotionAgent } from "../../Utilities/Domain/Promotions/PromotionAgent";
import { Permissions } from "../../Utilities/Domain/Users/Permissions";
import { type ShopfrontThunk } from "../../Utilities/Generic/ActionsHelper";
import { currency, roundTo } from "../../Utilities/Generic/Numbers";
import UUID from "../../Utilities/Generic/UUID";
import { getLogLevel, LogLevel } from "../../Utilities/Platform/Logging";
import { getOption, isTrainingMode } from "../../Utilities/Platform/Options/Options";
import { getVendorSubdomain } from "../../Utilities/Platform/Request";
import { showDialog, type ShowDialogAction } from "../Dialogs/DialogActions";
import { NoopAction, type NoopActionType } from "../General/MiscActions";
import { checkForLocalNetworkAccess } from "./PaymentLocalNetworkAccessPrompt";
import {
    addRefundReason,
    cancelSale,
    changeSellScreenActionMode,
    changeSellScreenSummaryMode,
    checkIfSaleIsComplete,
    finishSale,
    isSaleFinishing,
    type SaleFinishOptions,
    type SellScreenActionTypes,
} from "./SellScreenActions";
import { SALE_ADD_PAYMENT, SALE_REVERT_PAYMENT } from "./SellScreenConstants";
import { removeProductFromSale, requestGiftCardSource } from "./SellScreenProductActions";

export interface SaleAddPaymentOptions {
    cashout?: number | boolean | "auto";
    status?: SalePaymentStatus | "auto";
    exact?: boolean;
    rounding?: number | "auto";
    giftcard?: GiftCardPayment | "auto";
    refund: boolean;
    loyalty?: "auto" | number;
    vouchers?: boolean | Array<SaleVoucherProduct>;
    approve?: boolean;
    remove?: Array<number>;
    removeAmount?: number;
    receipt?: string;
    reverting?: boolean;
    voucherRefund?: boolean;
    related?: SalePayment & {
        index: number;
    };
    metaData?: Record<string, unknown>;
    source: string;
    subtype?: string;
}

export interface AddPaymentAction {
    type: typeof SALE_ADD_PAYMENT;
    payment: SalePayment;
}

export interface RevertPaymentAction {
    type: typeof SALE_REVERT_PAYMENT;
    index: number;
}

export type PaymentActionTypes = AddPaymentAction | RevertPaymentAction;

export function showDeletePayment(
    index: number,
    method: string,
    amount: number,
    cashout: number,
    rounding: number,
    salePayment: SalePayment,
    giftCard?: GiftCardPayment
): ShopfrontThunk<ShowDialogAction> {
    return (dispatch): Promise<void> => {
        return determineDataSource()
            .then(source => {
                if(method === "account") {
                    return {
                        name: "Account",
                        type: "account",
                    };
                } else if(method === "loyalty") {
                    return {
                        name: "Loyalty",
                        type: "loyalty",
                    };
                }

                return source.paymentMethods.get(method);
            })
            .then(paymentMethod => {
                dispatch(showDialog(
                    "Reverse Payment",
                    <RevertPayment
                        index={index}
                        method={method}
                        amount={amount}
                        cashout={cashout}
                        rounding={rounding}
                        name={paymentMethod.name}
                        giftCard={giftCard}
                        payment={salePayment}
                    />,
                    "danger",
                    false,
                    false
                ));
            });
    };
}

export function requestRefundReason(
    dispatch: ShopfrontDispatch,
    sale: SaleState,
    clearReasonIfRequired = false
): Promise<void> {
    if(!getOption("require_refund_reason")) {
        return Promise.resolve();
    }

    if(sale.refundReason && sale.refundReason.trim() !== "" && !clearReasonIfRequired) {
        return Promise.resolve();
    }

    let hasNegative = false;

    for(let i = 0, l = sale.products.length; i < l; i++) {
        if(sale.products[i].quantity < 0) {
            hasNegative = true;
            break;
        }
    }

    if(!hasNegative) {
        if(clearReasonIfRequired) {
            dispatch(addRefundReason(""));
        }

        return Promise.resolve();
    } else if(clearReasonIfRequired && sale.refundReason && sale.refundReason.trim() !== "") {
        return Promise.resolve();
    }

    return new Promise((res) => {
        requestAnimationFrame(() => {
            dispatch(showDialog(
                "Reason for Refund",
                <RefundReason onComplete={res} />,
                "question",
                false,
                false,
                false
            ));
        });
    });
}

export function requestRefund(
    method: string,
    amount: number,
    options: SaleAddPaymentOptions
): ShopfrontThunk<ShowDialogAction> {
    return (dispatch): Promise<void> => {
        return determineDataSource()
            .then(source => {
                if(method === "account") {
                    return {
                        name: "Account",
                        type: "account",
                    };
                } else if(method === "loyalty") {
                    return {
                        name: "Loyalty",
                        type: "loyalty",
                    };
                }

                return source.paymentMethods.get(method);
            })
            .then(paymentMethod => {
                dispatch(showDialog(
                    "Process Refund",
                    <RequestRefund
                        method={method}
                        amount={amount}
                        options={options}
                        name={paymentMethod.name}
                    />,
                    "danger",
                    false,
                    false
                ));
            });
    };
}

export function getGateWay(
    method: { type: string; gateway_url: string; },
    approve?: boolean
): [ url: string | null, needsLocalAccess: boolean ] {
    switch(method.type) {
        case "pc-eftpos":
        case "linkly-vaa":
            return [
                `/payment/pceftpos?shopfront-payment=true&dark-mode=${isDarkMode()}&vaa=${
                    method.type === "linkly-vaa"
                }`,
                true,
            ];
        case "tyro":
            return [ `/payment/tyro?shopfront-payment=true&dark-mode=${isDarkMode()}`, false ];
        case "custom":
            try {
                const url = new URL(method.gateway_url);

                url.searchParams.append("shopfront-payment", "true");
                url.searchParams.append("dark-mode", isDarkMode().toString());
                url.searchParams.append("vendor", getVendorSubdomain());

                return [ url.toString(), false ];
            } catch(e) {
                if(getLogLevel() >= LogLevel.NORMAL) {
                    console.error("Could not determine Gateway URL", e);
                }

                PopupManager.error(
                    "The Gateway URL seems to be incorrect, we will still try and display the page" +
                    " but it will not work offline"
                );

                return [ method.gateway_url, false ];
            }
        default:
            if(approve) {
                return [ `/payment/approve?shopfront-payment=true&dark-mode=${isDarkMode()}`, false ];
            } else {
                return [ null, false ];
            }
    }
}

export function showPaymentGateway(
    gateway: string,
    method: string,
    amount: number,
    options: SaleAddPaymentOptions,
    onComplete?: (method: string, amount: number, options: SaleAddPaymentOptions) => void,
    paymentId?: string
): ReturnType<typeof showDialog> {
    let isRetry = true;

    if(!paymentId) {
        // Generate a payment ID
        paymentId = UUID.generate();
        isRetry = false;
    }

    if(getLogLevel() === LogLevel.VERBOSE) {
        console.log("Showing a payment gateway", gateway, method, amount, options, paymentId);
    } else {
        console.log("Showing a payment gateway", gateway, method, amount);
    }

    return showDialog(
        "Payment Gateway",
        <Integration
            gateway={gateway}
            method={method}
            amount={amount}
            options={options}
            onComplete={onComplete}
            paymentId={paymentId}
            isRetry={isRetry}
        />,
        "question",
        false,
        false,
        false,
        false
    );
}

export function requestCashout(
    method: string,
    amount: number,
    options: SaleAddPaymentOptions
): ReturnType<typeof showDialog> {
    return showDialog(
        "Cash Out",
        <RequestCashout
            options={options}
            amount={amount}
            method={method}
        />,
        "question",
        false,
        false
    );
}

export function requestLoyalty(
    method: string,
    amount: number,
    options: SaleAddPaymentOptions
): ReturnType<typeof showDialog> {
    return showDialog(
        "Loyalty",
        <RequestLoyalty
            method={method}
            amount={amount}
            options={options}
        />,
        "question",
        false,
        false
    );
}

export function requestGiftCard(
    method: string,
    amount: number,
    options: SaleAddPaymentOptions
): ReturnType<typeof showDialog> {
    return showDialog(
        "Gift Card",
        <RequestGiftCard options={options} amount={amount} method={method} />,
        "question",
        false,
        false
    );
}

export function requestSelectGiftCard(
    code: string,
    method: string,
    amount: number,
    options: SaleAddPaymentOptions,
    found: Array<GiftCardPayment>
): ReturnType<typeof showDialog> {
    return showDialog(
        "Select Gift Card",
        <RequestSelectGiftCard
            code={code}
            options={options}
            amount={amount}
            method={method}
            found={found}
        />,
        "question",
        false,
        false
    );
}

export interface GiftCardPayment {
    id: string; // The UUID for internal gift cards, the code for external gift cards
    current_amount: number;
    original_amount: number;
    expiry: string;
    code: string;
    source: string;
}

export function requestPayGiftCard(
    method: string,
    amount: number,
    options: SaleAddPaymentOptions
): ReturnType<typeof showDialog> {
    return showDialog(
        "Pay with Gift Card",
        <RequestPayGiftCard
            options={options}
            amount={amount}
            method={method}
        />,
        "question",
        false,
        false
    );
}

let paymentAddingToSale = false;

export function addPaymentToSale(
    method: string,
    amount: number,
    partialOptions: Partial<SaleAddPaymentOptions>
): ShopfrontThunk<SellScreenActionTypes | NoopActionType> | AnyAction | NoopActionType {
    // Ensure that the promotion calculation isn't running
    if(PromotionAgent.getAgent().isCurrentlyCalculating()) {
        PopupManager.info(
            "Promotion calculation is currently running, please wait until it completes to get the correct price"
        );
        console.log("Promotion calculation is running, aborting");

        return NoopAction;
    }

    if(paymentAddingToSale) {
        console.log("Payment already adding to sale");

        return NoopAction;
    }

    if(isSaleFinishing()) {
        console.log("Sale is currently finishing, ignoring the payment");

        return NoopAction;
    }

    paymentAddingToSale = true;
    const defaultOptions: SaleAddPaymentOptions = {
        cashout      : "auto",
        status       : "auto",
        exact        : false,
        rounding     : "auto",
        giftcard     : "auto",
        refund       : false,
        loyalty      : "auto",
        reverting    : false,
        vouchers     : false,
        approve      : false,
        remove       : [], // Array of products to remove after sale
        removeAmount : 0,
        voucherRefund: false,
        metaData     : {},
        source       : "auto",
    };

    const options = Object.assign({}, defaultOptions, partialOptions);

    if(options.status === "auto" && doesDatabaseNeedUpdating()) {
        PopupManager.info(
            "Shopfront requires an update, please refresh the page before continuing"
        );

        paymentAddingToSale = false;

        return NoopAction;
    }

    console.log("Adding a payment to the sale", method, amount, options);

    return async (dispatch, getState): Promise<void> => {
        if((options.cashout === true || options.cashout === "auto") && !await Permissions.can("cashout")) {
            options.cashout = false;
        }

        const state = getState();

        // Ensure we're not adding a product to the sale
        if(state.sellscreen.sale.addingToSale !== null) {
            if(getLogLevel() >= LogLevel.NORMAL) {
                console.log("Product currently adding to sale", state.sellscreen.sale.addingToSale);
            }

            PopupManager.info(
                "A product is currently adding to the sale, " +
                "please wait until it has been added to get the correct price"
            );
            paymentAddingToSale = false;

            return;
        }

        if(!state.location.register?.id) {
            PopupManager.error("There doesn't appear to be a register active at the moment");

            return;
        }

        const sale = state.sellscreen.sale;

        const total = sale.totals.sale;

        let paid = sale.totals.paid;
        let remaining = sale.totals.remaining;

        await requestRefundReason(dispatch, sale);

        const allowCashoutWithoutSale = getOption("allow_cashout_without_sale");

        // Get the payment method
        const source = await determineDataSource();

        let paymentMethod: {
            name: string;
            type: string;
            use_rounding: boolean;
            cashout_method_id: null | string;
            gateway_url: string;
        };

        if(method === "account") {
            paymentMethod = {
                name             : "Account",
                type             : "account",
                use_rounding     : false,
                cashout_method_id: null,
                gateway_url      : "",
            };
        } else if(method === "loyalty") {
            paymentMethod = {
                name             : "Loyalty",
                type             : "loyalty",
                use_rounding     : false,
                cashout_method_id: null,
                gateway_url      : "",
            };
        } else {
            try {
                paymentMethod = await source.paymentMethods.get(method);
            } catch(err) {
                PopupManager.error("Error attempting payment, unable to find payment method");
                console.error("Unable to find payment method", err);
                paymentAddingToSale = false;

                return;
            }
        }

        if(
            paymentMethod.cashout_method_id === null &&
            typeof options.cashout === "number" &&
            options.cashout > 0
        ) {
            PopupManager.error(`Cashout is not supported for the ${paymentMethod.name} payment method`);
            console.error(
                `Unable to add a cashout amount to the ${paymentMethod.name} method as it doesn't support cashout`
            );

            paymentAddingToSale = false;

            return;
        }

        if(paymentMethod.use_rounding && !options.exact) {
            const parsed = parseFloat(amount.toString());

            // Calculate rounding
            const roundToAmount = getOption("round_to");

            if(roundToAmount !== 0) {
                const rounded = roundTo(parsed, roundToAmount);

                if(rounded !== parsed) {
                    PopupManager.warning("Invalid amount for this payment method");
                    paymentAddingToSale = false;

                    return;
                }
            }
        }

        if(total === 0 && sale.products.length === 0 && !(allowCashoutWithoutSale && !!options.cashout)) {
            if(getLogLevel() >= LogLevel.NORMAL) {
                console.log("Total is zero and not performing a cash out, aborting");
            }

            paymentAddingToSale = false;

            return;
        }

        // Check if exact should be used
        if(options.exact) {
            amount = remaining;
        }

        const refund = amount < 0;

        if(paymentMethod.type === "account") {
            if(sale.customer === false) {
                PopupManager.error("You require a customer to put a sale on account");
                paymentAddingToSale = false;

                return;
            }

            const customer = await source.customers.get(sale.customer.uuid);

            if(!customer.allow_account_sales) {
                PopupManager.error("The customer provided cannot put sales on account");
                paymentAddingToSale = false;

                return;
            }

            let totalOnAccount = amount;

            for(let i = 0, l = sale.payments.length; i < l; i++) {
                if(sale.payments[i].method === "account") {
                    totalOnAccount += sale.payments[i].amount;
                }
            }

            if(
                customer.account_limit !== null &&
                customer.account_limit < customer.current_owing + totalOnAccount
            ) {
                PopupManager.error("The customer doesn't have a high enough account limit to put this sale on account");
                paymentAddingToSale = false;

                return;
            }
        }

        // Check if refund dialog needs to be shown
        if(!options.refund && refund) {
            paymentAddingToSale = false;

            return dispatch(requestRefund(method, amount, options));
        }

        // Check if the loyalty amount needs to be fetched
        if(paymentMethod.type === "loyalty" && options.loyalty === "auto") {
            if(refund) {
                let redemptionRate = getOption("outlet_redemption_rate");

                if(isNaN(redemptionRate)) {
                    redemptionRate = 1;
                }

                options.loyalty = amount * redemptionRate;
            } else {
                paymentAddingToSale = false;

                return dispatch(requestLoyalty(method, amount, options));
            }
        }

        // Check if Gift Card needs to be fetched
        if(paymentMethod.type === "giftcard" && options.giftcard === "auto") {
            paymentAddingToSale = false;

            if(options.source === "auto") {
                return dispatch(requestGiftCardSource({ method, amount, options }));
            }

            return dispatch(requestGiftCard(method, amount, options));
        }

        // Parse amount
        amount = parseFloat(amount.toString());

        // Parse cashout
        let cashout = 0;

        if(options.cashout) {
            if(typeof options.cashout === "string") {
                const parsed = parseFloat(options.cashout);

                if(!isNaN(parsed)) {
                    cashout = parsed;
                }
            } else if(typeof options.cashout === "number") {
                if(!isNaN(options.cashout)) {
                    cashout = options.cashout;
                }
            }
        }

        // Check if no rounding is applied
        const noRounding =
            options.rounding === 0 ||
            options.rounding === "auto" ||
            typeof options.rounding === "undefined";

        // Check if requesting cash out
        const requestCashOut =
            allowCashoutWithoutSale &&
            (options.cashout === "auto" || !!cashout) &&
            paymentMethod.cashout_method_id;

        if(
            !requestCashOut &&
            amount === 0 &&
            noRounding
        ) {
            if(checkIfSaleIsComplete(total, remaining, paid) && sale.products.length > 0) {
                dispatch(finishSale("COMPLETED") as ShopfrontThunk);
            } else {
                PopupManager.warning("Please enter valid amount for payment");

                if(getLogLevel() === LogLevel.VERBOSE) {
                    console.log("Invalid amount entered for payment");
                }
            }

            paymentAddingToSale = false;

            return;
        }

        // Check if overpayment isn't allowed
        const overpayment = paymentMethod.type === "cash";

        if(!options.reverting) {
            if(refund) {
                let refundMin = remaining;
                const roundToAmount = getOption("round_to");

                if(paymentMethod.use_rounding && roundToAmount) {
                    refundMin = roundTo(remaining, roundToAmount);
                }

                if(remaining - amount > roundToAmount && !options.vouchers) {
                    PopupManager.warning("Overpayment not allowed for refunds");

                    if(getLogLevel() === LogLevel.VERBOSE) {
                        console.log("Overpayment attempted for a refund");
                    }

                    paymentAddingToSale = false;

                    return;
                } else if(refundMin === amount && paymentMethod.use_rounding) {
                    // Set the payment to the pre-rounding amount
                    amount = remaining;
                }
            } else if(!overpayment && remaining < amount) {
                if(options.status !== "auto") {
                    console.log(
                        "Received overpayment, however the sale appears to be from an integration so we're allowing it"
                    );
                } else {
                    PopupManager.warning("Only Payment Methods of 'Cash' type can have overpayment");

                    if(getLogLevel() === LogLevel.VERBOSE) {
                        console.log("Overpayment attempted for method that isn't cash");
                    }

                    paymentAddingToSale = false;

                    return;
                }
            }
        }

        // Check to see if surcharge should be added
        /*
        if (paymentMethod.surcharge) {
            // TODO: Add the surcharge here
        }
        */

        // Calculate rounding
        const roundToAmount = getOption("round_to");
        const rounding = paymentMethod.use_rounding && roundToAmount !== 0;

        if(options.rounding === "auto") {
            if(rounding) {
                // How many decimal places to round to
                const decimalSplit = roundToAmount.toString().split(".");

                let decimalLength = 1;

                if(decimalSplit.length > 1) {
                    decimalLength = Math.pow(10, decimalSplit[1].length);
                }

                const tempAmount = roundTo(amount, roundToAmount);

                options.rounding = (tempAmount * decimalLength - amount * decimalLength) / decimalLength;
                amount = tempAmount;
                cashout = roundTo(cashout, roundToAmount);
            }
        } else if(options.reverting && typeof options.rounding === "number") {
            remaining += options.rounding;
            paid -= options.rounding;
        }

        // Check if cashout should be requested
        if(options.cashout === true || (options.cashout === "auto" && paymentMethod.cashout_method_id)) {
            paymentAddingToSale = false;

            return dispatch(requestCashout(method, amount, options));
        }

        if(options.rounding === "auto") {
            options.rounding = 0;
        }

        if(options.cashout === "auto") {
            options.cashout = 0;
        }

        // Check if we need to check status of payment
        if(options.status === "auto") {
            const [ gateway, needsLocalAccess ] = getGateWay(paymentMethod, options.approve);

            if(gateway && !isTrainingMode()) {
                paymentAddingToSale = false;

                if(needsLocalAccess) {
                    const successful = await checkForLocalNetworkAccess();

                    if(!successful) {
                        return;
                    }
                }

                return dispatch(showPaymentGateway(gateway, method, amount, options));
            } else {
                options.status = "completed";
            }
        }

        if(options.status === "failed" || options.status === "declined") {
            // Show retry dialog
            dispatch(showDialog(
                "Retry Payment",
                <div>
                    <p>The current payment was declined, would you like to retry?</p>
                    <table>
                        <tr>
                            <th>Amount</th>
                            <td>{currency(amount)}</td>
                        </tr>
                        {typeof options.cashout === "number" && options.cashout !== 0 && (
                            <tr>
                                <th>Cash Out</th>
                                <td>{currency(options.cashout)}</td>
                            </tr>
                        )}
                    </table>
                </div>,
                "warning",
                [ CancelOptionButton, {
                    classes : [ "button-warning" ],
                    label   : "Retry",
                    callback: () => {
                        dispatch(addPaymentToSale(
                            method,
                            amount,
                            {
                                ...options,
                                status: "auto",
                            }
                        ));
                    },
                }]
            ));
        }

        if(options.related && (options.status === "completed" || options.status === "approved")) {
            dispatch({
                type : SALE_REVERT_PAYMENT,
                index: options.related.index,
            });
        }

        let metadata: string | Record<string, unknown> = options.metaData as Record<string, unknown>;

        if(options.giftcard !== "auto") {
            if(metadata === null) {
                metadata = {};
            }

            metadata["gift_card"] = options.giftcard;
        }

        if(options.loyalty !== "auto") {
            if(metadata === null) metadata = {};

            metadata.loyalty_points = options.loyalty;
        }

        const dispatched: SalePayment = {
            method       : method,
            type         : paymentMethod.type,
            status       : options.status as SalePaymentStatus,
            amount       : amount,
            metadata     : JSON.stringify(metadata ?? {}),
            cashout      : cashout,
            rounding     : options.rounding,
            registerId   : state.location.register.id,
            userId       : state.user.id === false ? "11e6d61b3dec425286c45cf9dd713698" : state.user.id,
            uploaded     : false,
            reverted     : !!options.reverting,
            receipt      : undefined,
            voucherRefund: options.voucherRefund,
            subtype      : options.subtype,
        };

        if(options.receipt) {
            dispatched.receipt = options.receipt;
        }

        if(getLogLevel() >= LogLevel.NORMAL) {
            console.log("Adding payment to a sale", dispatched);
        }

        dispatch({
            type   : SALE_ADD_PAYMENT,
            payment: dispatched,
        });

        dispatch(changeSellScreenSummaryMode("payments"));

        // Check if sale is already complete and vouchers are being processed
        const vouchers = !!options.vouchers;

        if(options.status === "completed" || vouchers) {
            remaining -= amount;
            paid += amount;

            // Round sale remaining
            if(rounding && Math.abs(remaining) < roundToAmount && !options.reverting) {
                remaining = roundTo(remaining, roundToAmount);
            }

            const saleIsFinished = checkIfSaleIsComplete(total, remaining, paid, refund, overpayment);

            if(saleIsFinished || vouchers) {
                const finishOptions: Partial<SaleFinishOptions> = {};

                if(options.vouchers && Array.isArray(options.vouchers)) {
                    finishOptions.vouchers = options.vouchers;

                    if(options.status === "failed" || options.status === "cancelled") {
                        finishOptions.voucherFailed = true;
                    } else {
                        finishOptions.vouchers.shift();
                    }
                } else if(options.vouchers && options.remove?.length !== 0) {
                    if(paid !== 0) {
                        paymentAddingToSale = false;

                        dispatch(addPaymentToSale(method, paid * -1, {
                            cashout     : false,
                            exact       : false,
                            vouchers    : true,
                            refund      : true,
                            remove      : options.remove,
                            removeAmount: (options.removeAmount || 0) + paid,
                        }));

                        return;
                    }
                }

                let removeCount = 0;

                if(Array.isArray(options.remove)) {
                    removeCount = options.remove.length;

                    for(let i = 0; i < removeCount; i++) {
                        console.log("Removing an item from the sale as it's complete");
                        dispatch(removeProductFromSale([ options.remove[i] ], false));
                    }
                }

                const productCount = sale.products.length;

                if(removeCount === productCount && productCount > 0) {
                    dispatch(cancelSale());
                    dispatch(changeSellScreenSummaryMode("transaction"));
                    dispatch(changeSellScreenActionMode("keys"));

                    if(typeof options.removeAmount === "number" && options.removeAmount !== 0) {
                        if(getLogLevel() === LogLevel.VERBOSE) {
                            console.log("Voucher refund required");
                        }

                        dispatch(showDialog(
                            "Voucher Refund",
                            <p>Was the refund of <NoTranslate>{currency(options.removeAmount)}</NoTranslate> successful?
                            </p>,
                            "question",
                            [{
                                ...CancelOptionButton,
                                label: "Yes",
                            }],
                            false,
                            false
                        ));
                    }
                } else {
                    dispatch(finishSale("COMPLETED", finishOptions));
                }
            }
        } else if(options.status === "cancelled" || options.status === "failed") {
            if(sale.products.length === 0) {
                dispatch(cancelSale());
                dispatch(changeSellScreenSummaryMode("transaction"));
                dispatch(changeSellScreenActionMode("keys"));
            }
        }

        paymentAddingToSale = false;
    };
}
